import { Content, Header } from '../../libs/gs-component-right-rail';
import kebabCase from 'lodash/kebabCase';
import pluralize from 'pluralize';
import React, { useContext, useMemo } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import BlankButton from '@xo-union/component-blank-button';

import { usePageQuery } from '../../generated/graphql';
import { previewRoute, dashboardRoute } from '../../helpers/routes';
import { TrackingContext } from '../../TrackingContext';
import GifItemField from './GifItemField';
import EventGroupItemField from './EventGroupItemField';
import { iconData, PageItemIcon } from './PageItemIcon';
import styles from './pageItemSelector.scss';
import PhotoItemField from './PhotoItemField';

interface Props {
  preview?: boolean;
  leftButton?: boolean;
  onCloseHandler?: () => void;
}

export const PageItemSelector: React.FC<Props> = ({ preview, leftButton, onCloseHandler }) => {
  const history = useHistory();
  const { pageId: pageIdString } = useParams<{ [key: string]: string }>();
  const pageId = parseInt(pageIdString || '');
  const { data, loading } = usePageQuery({
    variables: {
      id: pageId,
    },
  });
  const { track } = useContext(TrackingContext);
  const page = data?.page;

  // If page has an EventGroupItem item type, then that should appear first
  const pageItemTypes = useMemo((): string[] => {
    const itemTypes = page?.pageItemTypes || [];

    if (itemTypes.includes('EventGroupItem')) {
      const filteredItemTypes = itemTypes.filter(itemType => itemType !== 'EventGroupItem');

      return ['EventGroupItem', ...filteredItemTypes];
    }

    return itemTypes;
  }, [page]);

  const onDefaultItemIconClick = React.useCallback(
    (itemType: string) => {
      if (page) {
        track('Item Action Initiated', {
          action: 'add',
          actionName: `Add ${itemType}`,
          page: page.title,
          item: itemType,
          source: 'select item type',
        });
        history.push(`${preview ? previewRoute : dashboardRoute}/pages/${page.id}/${pluralize(kebabCase(itemType))}`);
      }
    },
    [history, page, preview, track]
  );

  if (!page) {
    return <div>Unknown Page</div>;
  }
  const rightRailBack = () => {
    history.push(`${preview ? previewRoute : dashboardRoute}`);
  };
  const pageItemDescription = () => {
    switch (page.type) {
      case 'StoryPage':
        return 'Tell your story with headlines, text blocks and photos!';
      case 'TravelPage':
        return 'Make it yours with headlines, text blocks and places to stay!';
      case 'ThingsToDoPage':
        return 'Make it yours with headlines, text blocks, and activities!';
      case 'WeddingPartyPage':
        return 'Make it yours by adding the members of your wedding party!';
      default:
        return 'Make it yours with headlines, text blocks, photos and photo galleries!';
    }
  };

  return (
    <>
      <Header
        onClose={!leftButton ? rightRailBack : onCloseHandler}
        as="h1"
        leftButtonText={leftButton ? 'Back' : ''}
        leftButtonOnClick={rightRailBack}
      >
        Add To Your Page
      </Header>
      <div className={styles.intro}>
        <span>{pageItemDescription()}</span>
      </div>
      <Content className={styles.container} loading={loading}>
        <div className={styles.buttons}>
          {pageItemTypes.map((itemType: string) => {
            // If no icon data we don't care about that item yet
            if (!iconData[itemType]) {
              return null;
            }

            if (itemType === 'PhotoItem') {
              return <PhotoItemField key={`PhotoItem-${pageId}`} pageId={pageId} />;
            } else if (itemType === 'GifItem') {
              return <GifItemField key={`GifItem-${pageId}`} pageId={pageId} />;
            } else if (itemType === 'EventGroupItem') {
              return <EventGroupItemField />;
            } else {
              return (
                <Link
                  className={styles.clickable}
                  key={`${itemType}-${page.id}`}
                  data-testid={`button-${itemType}`}
                  onClick={() => onDefaultItemIconClick(itemType)}
                  to={`${preview ? previewRoute : dashboardRoute}/pages/${page.id}/${pluralize(kebabCase(itemType))}`}
                  component={BlankButton}
                >
                  <PageItemIcon icon={itemType} />
                </Link>
              );
            }
          })}
        </div>
      </Content>
    </>
  );
};
